import { Plus, X } from "lucide-react";
import * as React from "react";
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";

import { useAuth } from "@/providers";
import {
  type ActionRecord,
  determineActionMemoryOptions,
  vCpuOptions,
  computeDefaultsOf,
  containerDefaultsOf,
  legalMemoryFor,
} from "@/shared/domain/actions";
import { OrganizationTier } from "@/shared/domain/orgs";
import {
  Card,
  Collapsible,
  Field,
  RobotoButton,
  Select,
  TextInput,
  type SelectItem,
} from "@/shared/foundation/primitives";

import styles from "./invocationSpec.module.css";

/** The longest an invocation may run, in minutes, on each tier. */
const TIMEOUT_MAX = { free: 30, premium: 720 };

const CPU_ITEMS: readonly SelectItem<string>[] = vCpuOptions.map((cpu) => ({
  value: String(cpu),
  label: `${cpu / 1024} vCPU`,
}));

/**
 * The shape a variable takes, shown as an example. Unlike the compute and
 * container fields, a variable has no setting of the action's own behind it
 * for the placeholder to offer.
 */
const ENV_VAR_EXAMPLE = { key: "LOG_LEVEL", value: "debug" };

/** The container settings, in the order the card shows them. */
const CONTAINER_FIELDS = [
  ["workdir", "Working directory"],
  ["entrypoint", "Entry point"],
  ["command", "Command"],
] as const;

export interface AdvancedSettingsProps {
  /** Where the invocation block lives in the enclosing form. */
  name: string;
  /** The chosen action, for the settings this only overrides. */
  action: ActionRecord | null;
}

/** Compute, container and timeout overrides on top of the action's own. */
export function AdvancedSettings({ name, action }: AdvancedSettingsProps) {
  const { control, setValue } = useFormContext();
  const { currentOrganization } = useAuth();
  const onFreeTier = currentOrganization?.tier === OrganizationTier.Free;
  const timeoutMax = onFreeTier ? TIMEOUT_MAX.free : TIMEOUT_MAX.premium;
  const cpu = useWatch({ control, name: `${name}.overrides.cpu` }) as
    | string
    | undefined;
  const memory = useWatch({ control, name: `${name}.overrides.memory` }) as
    | string
    | undefined;
  const compute = computeDefaultsOf(action);
  const container = containerDefaultsOf(action);
  const memoryItems: readonly SelectItem<string>[] =
    determineActionMemoryOptions(cpu ?? compute.cpu).map((amount) => ({
      value: String(amount),
      label: `${amount / 1024} GiB`,
    }));
  const envVars = useFieldArray({
    control,
    name: `${name}.overrides.envVars`,
  });
  const envNameId = React.useId();
  const envValueId = React.useId();
  // Per action, not per run: the runtime reads an unset flag as a download.
  const downloadsInputs =
    action === null ? null : action.requires_downloaded_inputs !== false;

  useSeededComputePair({ name, action, cpu, memory });

  return (
    <div data-testid="advanced-settings">
      <Collapsible.Root>
        <h4 className={styles.advancedTitle}>
          <Collapsible.Trigger>Advanced settings</Collapsible.Trigger>
        </h4>
        <Collapsible.Panel
          className={styles.advancedPanel}
          data-testid="advanced-settings-body"
        >
          <Card.Root size="compact" data-testid="compute-parameters">
            <Card.Header>
              <Card.Title>Compute parameters</Card.Title>
            </Card.Header>
            <Card.Body>
              <div className={styles.grid}>
                <Controller
                  control={control}
                  name={`${name}.overrides.cpu`}
                  render={({ field }) => (
                    <Select<string>
                      items={CPU_ITEMS}
                      value={
                        typeof field.value === "string" ? field.value : null
                      }
                      onValueChange={(chosen) => {
                        field.onChange(chosen);
                        if (chosen === null) {
                          return;
                        }
                        // The pair is validated together, so a vCPU that
                        // strands the memory beside it moves the memory too.
                        setValue(
                          `${name}.overrides.memory`,
                          legalMemoryFor(chosen, memory ?? compute.memory),
                          { shouldDirty: true },
                        );
                      }}
                      label="vCPU"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={`${name}.overrides.memory`}
                  render={({ field }) => (
                    <Select<string>
                      items={memoryItems}
                      value={
                        typeof field.value === "string" ? field.value : null
                      }
                      onValueChange={field.onChange}
                      label="Memory"
                      disabled={memoryItems.length === 0}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={`${name}.overrides.storage`}
                  render={({ field, fieldState }) => (
                    <Field.Root name={field.name} invalid={fieldState.invalid}>
                      <Field.Label>Storage (GiB)</Field.Label>
                      <Field.Control
                        render={<TextInput type="number" inputMode="numeric" />}
                        value={String(field.value ?? "")}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          const text = event.target.value;
                          field.onChange(
                            text === "" ? undefined : Number.parseInt(text, 10),
                          );
                        }}
                        onBlur={field.onBlur}
                        ref={field.ref}
                        placeholder={compute.storage}
                      />
                      <Field.Error match>
                        {fieldState.error?.message}
                      </Field.Error>
                    </Field.Root>
                  )}
                />
                <Controller
                  control={control}
                  name={`${name}.overrides.timeout`}
                  render={({ field, fieldState }) => (
                    <Field.Root name={field.name} invalid={fieldState.invalid}>
                      <Field.Label>Timeout (minutes)</Field.Label>
                      <Field.Control
                        render={
                          <TextInput
                            type="number"
                            inputMode="numeric"
                            max={timeoutMax}
                          />
                        }
                        value={String(field.value ?? "")}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          const text = event.target.value;
                          field.onChange(
                            text === "" ? null : Number.parseInt(text, 10),
                          );
                        }}
                        onBlur={field.onBlur}
                        placeholder={String(action?.timeout ?? timeoutMax)}
                      />
                      <Field.Description>
                        Up to {timeoutMax} minutes on this plan.
                      </Field.Description>
                      <Field.Error match>
                        {fieldState.error?.message}
                      </Field.Error>
                    </Field.Root>
                  )}
                />
              </div>
            </Card.Body>
          </Card.Root>

          <Card.Root size="compact" data-testid="container-parameters">
            <Card.Header>
              <Card.Title>Container parameters</Card.Title>
            </Card.Header>
            <Card.Body>
              <div className={styles.grid}>
                {CONTAINER_FIELDS.map(([key, label]) => (
                  <Controller
                    key={key}
                    control={control}
                    name={`${name}.overrides.${key}`}
                    render={({ field }) => (
                      <Field.Root name={field.name}>
                        <Field.Label>{label}</Field.Label>
                        <Field.Control
                          render={<TextInput spellCheck={false} />}
                          value={
                            typeof field.value === "string" ? field.value : ""
                          }
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          placeholder={
                            container[key] === ""
                              ? "The action's own"
                              : container[key]
                          }
                        />
                      </Field.Root>
                    )}
                  />
                ))}
              </div>
            </Card.Body>
          </Card.Root>

          <Card.Root size="compact" data-testid="environment-variables">
            <Card.Header>
              <Card.Title>Environment variables</Card.Title>
            </Card.Header>
            <Card.Body>
              <div className={styles.group}>
                {envVars.fields.length > 0 && (
                  <div
                    className={styles.envVarGrid}
                    data-testid="env-vars"
                    data-count={envVars.fields.length}
                  >
                    <span id={envNameId} className={styles.envVarHeading}>
                      Name
                    </span>
                    <span id={envValueId} className={styles.envVarHeading}>
                      Value
                    </span>
                    {envVars.fields.map((row, index) => (
                      <div
                        key={row.id}
                        className={styles.envVarRow}
                        data-testid={`env-var-${index}`}
                      >
                        <Controller
                          control={control}
                          name={`${name}.overrides.envVars.${index}.key`}
                          render={({ field }) => (
                            <TextInput
                              spellCheck={false}
                              aria-labelledby={envNameId}
                              placeholder={ENV_VAR_EXAMPLE.key}
                              value={
                                typeof field.value === "string"
                                  ? field.value
                                  : ""
                              }
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name={`${name}.overrides.envVars.${index}.value`}
                          render={({ field }) => (
                            <TextInput
                              spellCheck={false}
                              aria-labelledby={envValueId}
                              placeholder={ENV_VAR_EXAMPLE.value}
                              value={
                                typeof field.value === "string"
                                  ? field.value
                                  : ""
                              }
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                            />
                          )}
                        />
                        <RobotoButton
                          variant="ghost"
                          icon={<X aria-hidden />}
                          aria-label="Remove this environment variable"
                          onClick={() => {
                            envVars.remove(index);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}

                <RobotoButton
                  variant="secondary"
                  icon={<Plus aria-hidden size={16} />}
                  onClick={() => {
                    envVars.append({ key: "", value: "" });
                  }}
                  data-testid="add-env-var"
                >
                  Add variable
                </RobotoButton>
              </div>
            </Card.Body>
          </Card.Root>

          <p
            className={styles.hint}
            data-testid="download-inputs-note"
            data-downloads={downloadsInputs ?? undefined}
          >
            {downloadsInputs === null
              ? "Whether inputs are downloaded before the run is the action's own setting."
              : `This action ${downloadsInputs ? "downloads" : "does not download"} its inputs before the run.`}
          </p>
        </Collapsible.Panel>
      </Collapsible.Root>
    </div>
  );
}

/**
 * Fills in the vCPU and memory a stored target arrived without.
 *
 * The two are the only overrides the form states outright rather than leaving
 * to a placeholder: the amounts of memory on offer depend on the vCPU, so a
 * pair left blank would offer nothing to pick from. Choosing an action sets
 * them, which covers every target built in this form; a target loaded from a
 * trigger saved before they were stated needs them filled in here.
 */
function useSeededComputePair({
  name,
  action,
  cpu,
  memory,
}: {
  name: string;
  action: ActionRecord | null;
  cpu: string | undefined;
  memory: string | undefined;
}) {
  const { setValue } = useFormContext();

  React.useEffect(() => {
    if (action === null || (cpu !== undefined && memory !== undefined)) {
      return;
    }
    const defaults = computeDefaultsOf(action);
    if (cpu === undefined) {
      setValue(`${name}.overrides.cpu`, defaults.cpu);
    }
    if (memory === undefined) {
      // A target that stored a vCPU but no memory keeps that vCPU, so the
      // amount filled in has to be one that vCPU allows.
      setValue(
        `${name}.overrides.memory`,
        legalMemoryFor(cpu ?? defaults.cpu, defaults.memory),
      );
    }
  }, [action, cpu, memory, name, setValue]);
}
